import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { onUnauthorized } from "../api/client";
import { Account } from "../pages/Account";
import { Advanced } from "../pages/Advanced";
import { Alerts } from "../pages/Alerts";
import { Brief } from "../pages/Brief";
import { Events } from "../pages/Events";
import { Budget } from "../pages/Budget";
import { FeedPage } from "../pages/FeedPage";
import { Invite, Login } from "../pages/Gate";
import { Members } from "../pages/Members";
import { RecordPage } from "../pages/RecordPage";
import { Saved } from "../pages/Saved";
import { SharedRecord } from "../pages/SharedRecord";
import { Search } from "../pages/Search";
import { Tenants } from "../pages/Tenants";
import { keys, SessionProvider, useMe } from "./queries";
import { RouteError } from "./RouteError";
import { Shell } from "./Shell";

/** Signed in: the app. Otherwise: sign in. A 401 from any request drops back to sign-in. */
function Authenticated() {
  const me = useMe();
  const client = useQueryClient();
  // null means signed out (TanStack ignores setting undefined).
  useEffect(() => onUnauthorized(() => client.setQueryData(keys.me, null)), [client]);
  if (me.isPending) return null;
  if (me.isError || !me.data) return <Login />;
  return (
    <SessionProvider value={me.data}>
      <Shell />
    </SessionProvider>
  );
}

export const router = createBrowserRouter([
  { path: "/invite/:token", element: <Invite />, errorElement: <RouteError /> },
  // A publicly shared record: open to anyone with the link, signed in or not.
  { path: "/shared/:token", element: <SharedRecord />, errorElement: <RouteError /> },
  {
    element: <Authenticated />,
    // A failure in the shell itself (navigation, sign-in); a page's own failure stays inside the shell below.
    errorElement: <RouteError />,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Navigate to="/brief" replace /> },
          { path: "brief", element: <Brief /> },
          { path: "feeds/:id", element: <FeedPage /> },
          { path: "search", element: <Search /> },
          { path: "events", element: <Events /> },
          { path: "advanced", element: <Advanced /> },
          { path: "saved/:id", element: <Saved /> },
          { path: "alerts", element: <Alerts /> },
          { path: "alerts/:tab", element: <Alerts /> },
          { path: "budget", element: <Budget /> },
          { path: "budget/*", element: <Budget /> },
          { path: "records/:key", element: <RecordPage /> },
          { path: "members", element: <Members /> },
          { path: "account", element: <Account /> },
          { path: "tenants", element: <Tenants /> },
          { path: "*", element: <Navigate to="/brief" replace /> },
        ],
      },
    ],
  },
]);
